import { loadable, atomWithStorage } from 'jotai/utils';
import { atom } from 'jotai';
import { getMapDefaultConfig } from 'components/map/atoms';
import { atomWithLoadable, mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import { toastAtom as sharedToastAtom } from 'components/scenario-area-configs/atoms';
import type {
  DiscountRateScenarioResponse,
  EnumsResponse,
  SaveScenarioParams,
} from './types';

// ─────────────────────────────────────
// Re-export shared area config atoms
// ─────────────────────────────────────

export { sharedToastAtom as toastAtom };

export {
  categoriesBaseAtom,
  categoriesLoadableAtom,
  unitTypesBaseAtom,
  unitTypesLoadableAtom,
  areaCategoryAtom,
  areaQueryAtom,
  areaSearchLoadableAtom,
  unitTypeAtom,
  unitQueryAtom,
  unitSearchLoadableAtom,
  wellQueryAtom,
  wellSearchLoadableAtom,
  areaGeometriesAtom,
  fetchGeometryAtom,
  fetchGeometryLoadableAtom,
} from 'components/scenario-area-configs/atoms';

// ─────────────────────────────────────
// Map config — scoped to DRS feature
// ─────────────────────────────────────

export const drsMapConfigAtom = atomWithStorage(
  'insights-drs-scenario-map-config',
  getMapDefaultConfig({
    wellsV2: {
      checked: false,
      options: {
        surfaceHole: false,
        bottomHole: false,
        stick: false,
        survey: false,
        vertical: false,
        horizontal: false,
        horizontalPermit: false,
        horizontalDuc: false,
        horizontalProducing: false,
        horizontalShutIn: false,
        horizontalExpiredPermit: false,
        horizontalInactive: false,
        horizontalPrePermit: false,
        horizontalApd: false,
        horizontalProposal: false,
        horizontalPlanned: false,
        verticalPermit: false,
        verticalDuc: false,
        verticalProducing: false,
        verticalShutIn: false,
        verticalExpiredPermit: false,
        verticalInactive: false,
        verticalPrePermit: false,
        verticalApd: false,
        verticalProposal: false,
        verticalPlanned: false,
        permit: false,
        duc: false,
        producing: false,
        shutIn: false,
        expiredPermit: false,
        inactive: false,
        prePermit: false,
        apd: false,
        proposal: false,
        planned: false,
      },
    },
    developmentAreas: {
      checked: false,
      options: {
        horizontalUnits: false,
        verticalUnits: false,
        fieldUnits: false,
        pricing: false,
      },
    },
    basinTier: {
      checked: false,
      options: {
        basin: false,
        tier: false,
      },
    },
    titleEntityAssets: {
      checked: false,
      options: {
        surfaceInterest: false,
        workingInterest: false,
        unleased: false,
        royaltyInterest: false,
      },
    },
    scrollZoom: {
      checked: false,
    },
    popups: {
      checked: false,
    },
  })
);

// ─────────────────────────────────────
// DRS-specific: Enums
// ─────────────────────────────────────

/**
 * Fetches all DRS-related enum values for dropdown population.
 * @return {EnumsResponse} The enums response
 */
export const [enumsBaseAtom, enumsLoadableAtom] = atomWithLoadable(async () => {
  const response = await request.get<EnumsResponse>(
    '/discount_rate_scenarios/enums'
  );
  return response as EnumsResponse;
});

// ─────────────────────────────────────
// DRS-specific: Operator search
// ─────────────────────────────────────

export interface OperatorSearchResult {
  name: string;
  entityId: string | null;
}

interface OperatorSearchResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  results: OperatorSearchResult[];
}

/** The operator search query. undefined = no fetch, '' = fetch all, 'text' = filter. */
export const operatorQueryAtom = atom<string | undefined>(undefined);

/** Reactive fetch — runs when query becomes a string. */
const operatorSearchBaseAtom = atom(async (get) => {
  const query = get(operatorQueryAtom);
  if (query === undefined) return null;

  const response = await request.post<OperatorSearchResponse, unknown>(
    '/queries/operators',
    {
      text: query || undefined,
      page: 1,
      page_size: 20,
    },
    new AbortController()
  );

  if (response.type !== 'success') {
    throw new Error('Failed to search operators');
  }
  return response.data as OperatorSearchResponse;
});

export const operatorSearchLoadableAtom = loadable(operatorSearchBaseAtom);

// ─────────────────────────────────────
// DRS-specific: Save scenario
// ─────────────────────────────────────

/**
 * Saves a discount rate scenario via POST (create) or PATCH (update).
 * @param {SaveScenarioParams} params - The mode, optional ID, and payload
 * @return {DiscountRateScenarioResponse} The saved scenario response
 */
export const [saveScenarioAtom, saveScenarioLoadableAtom] =
  mutationAtomWithLoadable(
    async (
      _get,
      _set,
      params: SaveScenarioParams
    ): Promise<DiscountRateScenarioResponse> => {
      const url =
        params.mode === 'create'
          ? '/discount_rate_scenarios'
          : `/discount_rate_scenarios/${params.id}`;

      const method = params.mode === 'create' ? request.post : request.patch;

      const response = await method<DiscountRateScenarioResponse, unknown>(
        url,
        params.payload,
        new AbortController()
      );

      if (response.type !== 'success') {
        const data = response.type === 'error' ? response.data : undefined;
        const body = data as { errors?: { message: string }[] } | undefined;
        const message =
          body?.errors?.map((e) => e.message).join(', ') ||
          'Failed to save scenario';
        throw new Error(message);
      }

      return response.data;
    }
  );

// ─────────────────────────────────────
// DRS-specific: Clone scenario
// ─────────────────────────────────────

/**
 * Clones a discount rate scenario by ID.
 * @param {number} id - The scenario ID to clone
 * @return {DiscountRateScenarioResponse} The cloned scenario response
 */
export const [cloneScenarioAtom, cloneScenarioLoadableAtom] =
  mutationAtomWithLoadable(
    async (_get, set, id: number): Promise<DiscountRateScenarioResponse> => {
      const response = await request.post<
        DiscountRateScenarioResponse,
        unknown
      >(`/discount_rate_scenarios/${id}/clone`, {}, new AbortController());

      if (response.type !== 'success') {
        set(sharedToastAtom, {
          message: 'Failed to clone scenario',
          type: 'error',
        });
        throw new Error('Failed to clone scenario');
      }

      return response.data;
    }
  );

// ─────────────────────────────────────
// DRS-specific: Fetch scenario (edit mode)
// ─────────────────────────────────────

/**
 * Fetches a discount rate scenario by ID for edit mode.
 * @param {number} id - The scenario ID
 * @return {DiscountRateScenarioResponse} The scenario response
 */
export const [fetchScenarioAtom, fetchScenarioLoadableAtom] =
  mutationAtomWithLoadable(
    async (_get, _set, id: number): Promise<DiscountRateScenarioResponse> => {
      const response = await request.get<DiscountRateScenarioResponse>(
        `/discount_rate_scenarios/${id}`
      );

      if (!response || 'error' in response) {
        throw new Error(
          (response as { error: string })?.error ?? 'Failed to load scenario'
        );
      }

      return response;
    }
  );
